/**
 * mockMath.ts — the authoritative 3-layer outcome roller used by MockTransport.
 *
 * Implements the EXACT model in math/config.json:
 *
 *  LAYER 1 (drip): roll a per-color total drip multiple D from the drip table.
 *    The total = D * stake is split across the color's cells as sub-amounts,
 *    revealed one per cell. A few cells are flagged "sparkle".
 *
 *  LAYER 2 (color-complete): roll a stardust multiple C (>= floor, always > 0)
 *    from the color-complete table; paid on the LAST cell of the color.
 *
 *  LAYER 3 (picture-complete): roll a multiple P from the picture table; the
 *    awarded amount = P * total_stake * (colorsCompleted / K)^1.5. Rolled once
 *    when the final color finishes; coverage<1 strictly reduces it (FOMO).
 *
 * Everything is driven by a seeded RNG over (serverSeed, clientSeed, nonce, betId)
 * so outcomes are reproducible and "provably fair"-shaped.
 */
import { Rng } from '../util/rng';
import {
  COLOR_COMPLETE_TABLE,
  COVERAGE_EXPONENT,
  DRIP_TABLE,
  MAX_PIC_MULT,
  PICTURE_TABLE,
  STARDUST_FLOOR,
} from './mathConfig';

export interface ColorOutcome {
  betId: string;
  stake: number;
  cellCount: number;
  /** total drip multiple of stake (layer 1). */
  dripMult: number;
  /** per-cell drip multiples (sum == dripMult), aligned to cell reveal order. */
  cellMults: number[];
  /** per-cell absolute sub-amounts (== cellMults * stake). */
  cellAmounts: number[];
  /** which cells sparkle. */
  cellSparkle: boolean[];
  /** color-complete stardust multiple (layer 2, always > 0). */
  colorCompleteMult: number;
  colorCompleteAmount: number;
  /** raw draw values for fairness display. */
  draws: number[];
}

export interface PictureOutcome {
  /** rolled multiple of total stake (pre-coverage). */
  rolledMult: number;
  /** coverage factor (done/K)^1.5. */
  coverage: number;
  /** final amount = rolledMult * totalStake * coverage. */
  amount: number;
  /** effective multiple of total stake after coverage. */
  effectiveMult: number;
  colorsCompleted: number;
  K: number;
  tier: 'none' | 'nice' | 'big' | 'mega' | 'legendary';
}

function weightedRoll(rng: Rng, table: typeof DRIP_TABLE): number {
  return rng.weighted(table.mults, table.weights);
}

/**
 * Roll a complete per-color outcome and distribute the drip total across cells.
 */
export function rollColorOutcome(
  seedStr: string,
  betId: string,
  stake: number,
  cellCount: number,
): ColorOutcome {
  const rng = new Rng(`${seedStr}:${betId}`);
  const draws: number[] = [];

  // LAYER 1 — total drip multiple
  const dripMult = weightedRoll(rng, DRIP_TABLE);
  draws.push(dripMult);

  // distribute dripMult across cells with organic variance (some 0, some bursts)
  const cellMults = distributeDrip(rng, dripMult, cellCount);
  const cellAmounts = cellMults.map((m) => +(m * stake).toFixed(6));

  // sparkle on a few of the higher cells + a couple random
  const cellSparkle = new Array<boolean>(cellCount).fill(false);
  const order = cellMults
    .map((m, i) => ({ m, i }))
    .sort((a, b) => b.m - a.m);
  const sparkles = Math.min(cellCount, Math.max(1, Math.round(cellCount * 0.12)));
  for (let i = 0; i < sparkles; i++) {
    if (order[i] && order[i].m > 0) cellSparkle[order[i].i] = true;
  }

  // LAYER 2 — color-complete stardust (always > 0)
  let ccMult = weightedRoll(rng, COLOR_COMPLETE_TABLE);
  if (ccMult <= 0) ccMult = STARDUST_FLOOR;
  ccMult = Math.max(STARDUST_FLOOR, ccMult);
  draws.push(ccMult);

  return {
    betId,
    stake,
    cellCount,
    dripMult,
    cellMults,
    cellAmounts,
    cellSparkle,
    colorCompleteMult: ccMult,
    colorCompleteAmount: +(ccMult * stake).toFixed(6),
    draws,
  };
}

/**
 * Spread a total drip multiple over N cells. Produces a pleasing reveal:
 * many small or zero cells with occasional bigger pops; exact sum preserved.
 */
function distributeDrip(rng: Rng, total: number, n: number): number[] {
  if (n <= 0) return [];
  if (total <= 0) return new Array<number>(n).fill(0);
  // assign random positive weights, zero out a fraction of cells
  const weights: number[] = [];
  let zeroFrac = 0.32; // ~third of cells reveal nothing (loss-disguise)
  for (let i = 0; i < n; i++) {
    if (rng.float() < zeroFrac) weights.push(0);
    else weights.push(Math.pow(rng.float(), 1.8) + 0.04);
  }
  let sum = weights.reduce((s, w) => s + w, 0);
  if (sum <= 0) {
    weights[rng.int(0, n - 1)] = 1;
    sum = 1;
  }
  const out = weights.map((w) => (w / sum) * total);
  // round to cents-ish granularity but preserve sum on the last nonzero cell
  let acc = 0;
  for (let i = 0; i < n; i++) {
    out[i] = Math.round(out[i] * 1000) / 1000;
    acc += out[i];
  }
  const drift = total - acc;
  // push drift onto the largest cell
  let maxI = 0;
  for (let i = 1; i < n; i++) if (out[i] > out[maxI]) maxI = i;
  out[maxI] = Math.max(0, Math.round((out[maxI] + drift) * 1000) / 1000);
  return out;
}

function pictureTier(effMult: number): PictureOutcome['tier'] {
  if (effMult <= 0) return 'none';
  if (effMult >= 200) return 'legendary';
  if (effMult >= 50) return 'mega';
  if (effMult >= 10) return 'big';
  return 'nice';
}

/**
 * Roll the picture-complete bonus. colorsCompleted/K is the coverage; the
 * coverage factor (done/K)^1.5 reduces partial completions (FOMO penalty).
 */
export function rollPictureOutcome(
  seedStr: string,
  pictureInstanceId: string,
  totalStake: number,
  colorsCompleted: number,
  K: number,
): PictureOutcome {
  const rng = new Rng(`${seedStr}:pic:${pictureInstanceId}`);
  let rolledMult = weightedRoll(rng, PICTURE_TABLE);
  rolledMult = Math.min(rolledMult, MAX_PIC_MULT);
  const coverage = Math.pow(Math.max(0, colorsCompleted) / Math.max(1, K), COVERAGE_EXPONENT);
  const amount = +(rolledMult * totalStake * coverage).toFixed(6);
  const effectiveMult = rolledMult * coverage;
  return {
    rolledMult,
    coverage,
    amount,
    effectiveMult,
    colorsCompleted,
    K,
    tier: pictureTier(effectiveMult),
  };
}

/**
 * Teaser projection for the FOMO HUD: an expected-value-ish hint of the picture
 * bonus the player is "leaving on the table" if they stop now vs. finishing.
 * (Display-only; not a guarantee.)
 */
export function projectedTeaser(
  totalStake: number,
  colorsRemaining: number,
  K: number,
): number {
  // expected fraction of total stake at full coverage, scaled by remaining gap
  const fullEV = 0.14 * totalStake; // expected_fraction_of_total_stake
  const remainingFactor = Math.min(1, (colorsRemaining + 0.5) / Math.max(1, K));
  // headline teaser leans toward the upside to create pull, capped sensibly
  return +(fullEV * (3 + remainingFactor * 6)).toFixed(2);
}
